import { ReactNode, useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useGetCreditCardProductsQuery } from "@lending/hooks/queries/CreditCardProduct/useGetCreditCardProductsQuery";
import { useGetEligibilityCriteriaQuery } from "@lending/hooks/queries/EligibilityCriteria/useGetEligibilityCriteriaQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useFlowNavigation } from "@lending/navigation";
import { isMlaQuestionEnabled } from "@lending/pages/Common/ApplicationQuestions/mlaQuestion";

interface CreditCardGuardProps {
  children?: ReactNode;
}

function CreditCardGuard({ children }: CreditCardGuardProps) {
  const { creditCardProductsData } = useGetCreditCardProductsQuery();
  const { hasEligibilityCriteria } = useGetEligibilityCriteriaQuery();
  const { institution } = useGetInstitutionQuery();
  const hasLendingQuestions = isMlaQuestionEnabled(institution.lending_ask_additional_fields);
  const { startFlow } = useFlowNavigation();
  const location = useLocation();
  const hasStartedRef = useRef(false);

  const numCreditCardProducts = creditCardProductsData?.count ?? 0;
  const hasCreditCardProducts = numCreditCardProducts > 0;
  const isBarePath = location.pathname === "/credit-card" || location.pathname === "/credit-card/";

  // Mirror the product-selection button on the bare product URL: startFlow
  // both clears stale flow state (so useFlowNavigation reads the right
  // productType) and navigates via useNavigateWithSearch (preserving any
  // query params). The ref guards against startFlow's unstable reference
  // re-firing the effect and re-triggering updateData on every render.
  useEffect(() => {
    if (!isBarePath) {
      hasStartedRef.current = false;
      return;
    }
    if (hasStartedRef.current || !hasCreditCardProducts) return;
    hasStartedRef.current = true;
    startFlow(
      "credit-card",
      {
        hasEligibilityCriteria,
        hasLendingQuestions,
        hasMultipleCreditCardProducts: numCreditCardProducts > 1,
      },
      { navigateOptions: { replace: true } }
    );
  }, [
    isBarePath,
    hasCreditCardProducts,
    hasEligibilityCriteria,
    hasLendingQuestions,
    numCreditCardProducts,
    startFlow,
  ]);

  if (!hasCreditCardProducts) {
    return <Navigate to="/" replace />;
  }

  if (isBarePath) {
    return null;
  }

  return children ? <>{children}</> : <Outlet />;
}

export { CreditCardGuard };
